// src/pages/vendor/VendorProducts.jsx
import React, { useEffect, useRef, useState } from "react";
import {
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
  addDoc,
  serverTimestamp,
  updateDoc,
  doc,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { auth, db, storage } from "../../firebase";
import { fetchTopCategories } from "../../lib/categories";

/**
 * Cette page est prévue pour être utilisée :
 * - côté vendeur, derrière <ProtectedVendorRoute> qui injecte userDoc
 *   <ProtectedVendorRoute>
 *     <VendorProducts />
 *   </ProtectedVendorRoute>
 *
 * - côté admin, via AdminVendorProductsWrapper :
 *   <VendorProducts adminMode={true} adminVendorId="..." userDoc={vendorDoc} />
 */
export default function VendorProducts({
  userDoc,
  adminMode = false,
  adminVendorId = null,
}) {
  const [products, setProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(true);

  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);

  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");

  const [name, setName] = useState("");
  const [shortName, setShortName] = useState("");
  const [description, setDescription] = useState("");
  const [barcode, setBarcode] = useState("");
  const [SalePrice, setSalePrice] = useState("");
  const [stock, setStock] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [subCategoryId, setSubCategoryId] = useState("");
  const [mainImageFile, setMainImageFile] = useState(null);
  const [extraImageFiles, setExtraImageFiles] = useState([]);

  const [editingProduct, setEditingProduct] = useState(null);

  const nameInputRef = useRef(null);

  const firebaseUser = auth.currentUser;

  const vendorId = adminMode ? adminVendorId : firebaseUser?.uid || null;

  const vendorName = adminMode
    ? userDoc?.shopName ||
      userDoc?.displayName ||
      userDoc?.email ||
      "Vendeur GoShop"
    : userDoc?.shopName ||
      userDoc?.displayName ||
      firebaseUser?.email ||
      "Mon Shop";
useEffect(() => {
  if (!vendorId) {
    setLoadingProducts(false);
    return;
  }

  setLoadingProducts(true);

  const qProducts = query(
    collection(db, "productsShop"),
    where("vendorId", "==", vendorId),
    orderBy("__name__", "desc")
  );

  const unsub = onSnapshot(
    qProducts,
    (snap) => {
      const items = snap.docs.map((d) => ({
        id: d.id,
        ...d.data(),
      }));
      setProducts(items);
      setLoadingProducts(false);
    },
    (err) => {
      console.error("Erreur chargement produits vendeur:", err);
      setLoadingProducts(false);
    }
  );

  return () => unsub();
}, [vendorId]);
  // useEffect(() => {
  //   if (!vendorId) {
  //     setLoadingProducts(false);
  //     return;
  //   }

  //   const qProducts = query(
  //     collection(db, "productsShop"),
  //     where("vendorId", "==", vendorId),
  //     orderBy("createdAt", "desc")
  //   );

  //   const unsub = onSnapshot(
  //     qProducts,
  //     (snap) => {
  //       const items = snap.docs.map((d) => ({
  //         id: d.id,
  //         ...d.data(),
  //       }));
  //       setProducts(items);
  //       setLoadingProducts(false);
  //     },
  //     (err) => {
  //       console.error("Erreur chargement produits vendeur:", err);
  //       setLoadingProducts(false);
  //     }
  //   );

  //   return () => unsub();
  // }, [vendorId]);

  useEffect(() => {
    const loadCats = async () => {
      try {
        const tops = await fetchTopCategories();
        setCategories(tops);

        const subs = tops.flatMap((c) =>
          (c.children || []).map((sc) => ({
            ...sc,
            parentId: c.id,
          }))
        );
        setSubCategories(subs);
      } catch (err) {
        console.error("Erreur chargement catégories:", err);
      }
    };

    loadCats();
  }, []);

  useEffect(() => {
    if (showForm && nameInputRef.current) {
      const timer = setTimeout(() => {
        nameInputRef.current?.focus();
        nameInputRef.current?.select();
      }, 80);

      return () => clearTimeout(timer);
    }
  }, [showForm, editingProduct]);

  const resetForm = () => {
    setName("");
    setShortName("");
    setDescription("");
    setBarcode("");
    setSalePrice("");
    setStock("");
    setCategoryId("");
    setSubCategoryId("");
    setMainImageFile(null);
    setExtraImageFiles([]);
    setError("");
    setInfo("");
    setEditingProduct(null);
  };

  const handleMainImageChange = (e) => {
    const file = e.target.files?.[0] || null;
    setMainImageFile(file);
  };

  const handleExtraImagesChange = (e) => {
    const files = Array.from(e.target.files || []);
    setExtraImageFiles(files);
  };

  const buildSearchKeywords = (label = "") => {
    const parts = label
      .toLowerCase()
      .split(/[ ,.;:/&()]+/)
      .filter(Boolean);

    const set = new Set(parts);
    return Array.from(set);
  };

  const handleEditClick = (product) => {
    setEditingProduct(product);
    setShowForm(true);
    setError("");
    setInfo("");

    const catId = product.categoryId || product.Categorie?.Id || "";
    const subId =
      product.subCategoryId || product.Categorie?.SubCategorie?.Id || "";

    setName(product.name || "");
    setShortName(product.shortName || product.name || "");
    setDescription(product.description || "");
    setBarcode(product.barcode || "");

    setSalePrice(
      typeof product.SalePrice === "number"
        ? product.SalePrice.toString()
        : typeof product.Cass_Stock_Price === "number"
        ? product.Cass_Stock_Price.toString()
        : ""
    );

    setStock(
      typeof product.stock === "number" ? product.stock.toString() : ""
    );

    setCategoryId(catId);
    setSubCategoryId(subId);

    setMainImageFile(null);
    setExtraImageFiles([]);
  };

  const handleSubmitProduct = async (e) => {
    e.preventDefault();
    setError("");
    setInfo("");

    try {
      if (!vendorId) {
        setError("Vous devez être connecté en tant que vendeur.");
        return;
      }

      if (!name.trim() || !SalePrice) {
        setError("Le nom du produit et le prix de vente sont obligatoires.");
        return;
      }

      if (!categoryId) {
        setError("Veuillez sélectionner une catégorie.");
        return;
      }

      setSaving(true);

      const selectedCat = categories.find((c) => c.id === categoryId);
      const selectedSub = subCategories.find((c) => c.id === subCategoryId);

      const categoryLabel = selectedCat?.label || "";
      const subCategoryLabel = selectedSub?.label || "";

      const now = serverTimestamp();
      const priceNumber = Number(SalePrice);

      const categorieMap = {
        Id: categoryId,
        Label: categoryLabel,
        SubCategorie: subCategoryId
          ? {
              Id: subCategoryId,
              Label: subCategoryLabel,
            }
          : null,
      };

      const payloadCommon = {
        vendorId,
        vendorName,
        vendorStatus: userDoc?.status || "active",

        name: name.trim(),
        shortName: (shortName || name).trim(),
        description: description.trim(),
        barcode: barcode.trim() || null,
        sku: null,

        categoryId,
        categoryLabel,
        subCategoryId: subCategoryId || null,
        subCategoryLabel: subCategoryLabel || null,
        sousCategorie: subCategoryLabel || categoryLabel || "",
        Categorie: categorieMap,

        currency: "XOF",
        SalePrice: priceNumber,
        price: priceNumber,
        Price: priceNumber,
        Cass_Stock_Price: priceNumber,
        oldPrice: null,
        buyPriceUSD: null,
        fixedPrice: false,
        lockPrice: false,
        stock: stock ? Number(stock) : 0,
        outOfStock: stock ? Number(stock) <= 0 : true,

        searchName: name.toLowerCase(),
        searchKeywords: buildSearchKeywords(name),
        tags: subCategoryLabel ? [subCategoryLabel] : [],

        updatedAt: now,
      };

      if (!editingProduct) {
        const baseData = {
          ...payloadCommon,
          isValidated: false,
          validationStatus: "pending",
          isVisible: false,
          adminNote: null,
          ImageUrl: "",
          Images: [],
          createdAt: now,
          createdBy: {
            userId: vendorId,
            userName: vendorName,
          },
        };

        const docRef = await addDoc(collection(db, "productsShop"), baseData);

        let mainUrl = "";
        const allUrls = [];

        if (mainImageFile) {
          const ext = mainImageFile.name.split(".").pop() || "jpg";
          const storageRef = ref(
            storage,
            `productsShop/${vendorId}/${docRef.id}/main-${Date.now()}.${ext}`
          );
          await uploadBytes(storageRef, mainImageFile);
          mainUrl = await getDownloadURL(storageRef);
          allUrls.push(mainUrl);
        }

        if (extraImageFiles.length > 0) {
          for (const file of extraImageFiles) {
            const ext = file.name.split(".").pop() || "jpg";
            const storageRef = ref(
              storage,
              `productsShop/${vendorId}/${docRef.id}/img-${Date.now()}-${file.name.replace(
                /\s+/g,
                "_"
              )}.${ext}`
            );
            await uploadBytes(storageRef, file);
            const url = await getDownloadURL(storageRef);
            allUrls.push(url);
          }
        }

        if (allUrls.length > 0) {
          await updateDoc(docRef, {
            ImageUrl: mainUrl || allUrls[0],
            Images: allUrls,
            updatedAt: serverTimestamp(),
          });
        }

        setInfo(
          "Votre produit a été enregistré en attente de validation par l’équipe GoShop."
        );
      } else {
        const productRef = doc(db, "productsShop", editingProduct.id);

        const existingImages = Array.isArray(editingProduct.Images)
          ? [...editingProduct.Images]
          : editingProduct.ImageUrl
          ? [editingProduct.ImageUrl]
          : [];

        let newMainUrl = editingProduct.ImageUrl || "";
        let finalImages = [...existingImages];

        if (mainImageFile) {
          const ext = mainImageFile.name.split(".").pop() || "jpg";
          const storageRef = ref(
            storage,
            `productsShop/${vendorId}/${editingProduct.id}/main-${Date.now()}.${ext}`
          );
          await uploadBytes(storageRef, mainImageFile);
          newMainUrl = await getDownloadURL(storageRef);

          finalImages = [
            newMainUrl,
            ...finalImages.filter((u) => u !== newMainUrl),
          ];
        }

        if (extraImageFiles.length > 0) {
          for (const file of extraImageFiles) {
            const ext = file.name.split(".").pop() || "jpg";
            const storageRef = ref(
              storage,
              `productsShop/${vendorId}/${editingProduct.id}/img-${Date.now()}-${file.name.replace(
                /\s+/g,
                "_"
              )}.${ext}`
            );
            await uploadBytes(storageRef, file);
            const url = await getDownloadURL(storageRef);
            finalImages.push(url);
          }
        }

        finalImages = Array.from(new Set(finalImages));

        const wasValidated =
          editingProduct.validationStatus === "validated" ||
          editingProduct.isValidated === true;

        const updateData = {
          ...payloadCommon,
          ImageUrl: newMainUrl || finalImages[0] || "",
          Images: finalImages,
          lastEditByVendorAt: now,
          lastEditByVendorId: vendorId,
          lastEditNeedsReview: true,
        };

        if (wasValidated) {
          updateData.validationStatus = "pending";
          updateData.isValidated = false;
          updateData.adminNote = null;
        }

        await updateDoc(productRef, updateData);

        await addDoc(collection(db, "adminProductEvents"), {
          type: "vendor_edit",
          vendorId,
          vendorName,
          productId: editingProduct.id,
          productName: name.trim(),
          createdAt: serverTimestamp(),
          isRead: false,
          source: "vendor-dashboard",
        });

        setInfo(
          "Votre produit a été mis à jour et repasse en attente de validation par l’équipe GoShop."
        );
      }

      resetForm();
      setShowForm(false);
    } catch (err) {
      console.error("Erreur création/édition produit:", err);
      setError(
        "Une erreur est survenue lors de l’enregistrement du produit."
      );
    } finally {
      setSaving(false);
    }
  };

  const renderStatusBadge = (product) => {
    const s =
      product.validationStatus ||
      (product.isValidated ? "validated" : "pending");

    if (s === "validated") {
      return <span className="vp-status vp-status-valid">Publié</span>;
    }
    if (s === "rejected") {
      return <span className="vp-status vp-status-refused">Refusé</span>;
    }
    return (
      <span className="vp-status vp-status-pending">
        En attente de validation
      </span>
    );
  };

  const existingImages = editingProduct
    ? Array.isArray(editingProduct.Images) && editingProduct.Images.length > 0
      ? editingProduct.Images
      : editingProduct.ImageUrl
      ? [editingProduct.ImageUrl]
      : []
    : [];

  return (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        padding: "24px 16px 40px",
      }}
    >
      <main
        style={{
          width: "100%",
          maxWidth: "1120px",
          fontSize: "14px",
          lineHeight: 1.5,
        }}
      >
        <header style={{ marginBottom: 16 }}>
          <h1
            style={{
              fontSize: 22,
              fontWeight: 800,
              color: "#0f172a",
              marginBottom: 4,
            }}
          >
            {adminMode ? "Produits du vendeur" : "Mes produits"}
          </h1>
          <p
            style={{
              fontSize: 13,
              color: "#4b5563",
              marginBottom: 12,
            }}
          >
            {adminMode
              ? "Vous consultez les produits créés par ce vendeur. Les validations / refus se gèrent dans l’interface d’administration."
              : "Gérez les produits que vous vendez sur GoShop. Les nouveaux produits et les modifications sont soumis à validation par l’équipe GoShop avant d’être affichés sur le site."}
          </p>

          {!adminMode && (
            <button
              type="button"
              onClick={() => {
                if (editingProduct) {
                  resetForm();
                }
                setShowForm((prev) => !prev);
              }}
              className="vp-btn-primary"
            >
              {showForm
                ? editingProduct
                  ? "Fermer le formulaire d’édition"
                  : "Fermer le formulaire"
                : "Ajouter un nouveau produit"}
            </button>
          )}
        </header>

        {error && (
          <div className="vp-alert-error" style={{ marginBottom: 10 }}>
            {error}
          </div>
        )}
        {info && (
          <div
            style={{
              marginBottom: 10,
              padding: "8px 10px",
              borderRadius: 10,
              background: "#ecfdf5",
              color: "#166534",
              fontSize: 13,
            }}
          >
            {info}
          </div>
        )}

        {!adminMode && showForm && (
          <section className="vp-card vp-form-card">
            <h2 className="vp-form-title">
              {editingProduct ? "Modifier le produit" : "Nouveau produit"}
            </h2>
            <p className="vp-form-subtitle">
              {editingProduct
                ? "Mettez à jour les informations de votre produit. Après modification, il sera à nouveau vérifié par l’admin."
                : "Complétez les informations ci-dessous. Après validation par un admin, votre produit sera visible sur la boutique."}
            </p>

            <form onSubmit={handleSubmitProduct} className="vp-form-grid">
              <div className="vp-form-group">
                <label>
                  Nom du produit <span className="vp-required">*</span>
                </label>
                <input
                  ref={nameInputRef}
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Ex : CeraVe SA Cream for Rough & Bumpy Skin"
                  required
                />
              </div>

              <div className="vp-form-group">
                <label>Nom court (pour les cartes)</label>
                <input
                  type="text"
                  value={shortName}
                  onChange={(e) => setShortName(e.target.value)}
                  placeholder="Ex : CeraVe SA Cream 340g"
                />
              </div>

              <div className="vp-form-group">
                <label>
                  Catégorie <span className="vp-required">*</span>
                </label>
                <select
                  value={categoryId}
                  onChange={(e) => {
                    setCategoryId(e.target.value);
                    setSubCategoryId("");
                  }}
                  required
                >
                  <option value="">Sélectionner une catégorie</option>
                  {categories.map((cat) => (
                    <option key={cat.id} value={cat.id}>
                      {cat.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="vp-form-group">
                <label>Sous-catégorie</label>
                <select
                  value={subCategoryId}
                  onChange={(e) => setSubCategoryId(e.target.value)}
                  disabled={!categoryId}
                >
                  <option value="">(optionnel)</option>
                  {subCategories
                    .filter((sub) => sub.parentId === categoryId)
                    .map((sub) => (
                      <option key={sub.id} value={sub.id}>
                        {sub.label}
                      </option>
                    ))}
                </select>
              </div>

              <div className="vp-form-group">
                <label>
                  Prix de vente (FCFA) <span className="vp-required">*</span>
                </label>
                <input
                  type="number"
                  min="0"
                  value={SalePrice}
                  onChange={(e) => setSalePrice(e.target.value)}
                  placeholder="Ex : 23500"
                  required
                />
                <small className="vp-help-text">
                  Sera également enregistré dans le champ{" "}
                  <code>Cass_Stock_Price</code>.
                </small>
              </div>

              <div className="vp-form-group">
                <label>Stock disponible</label>
                <input
                  type="number"
                  min="0"
                  value={stock}
                  onChange={(e) => setStock(e.target.value)}
                  placeholder="Ex : 10"
                />
              </div>

              <div className="vp-form-group">
                <label>Code-barres / UPC</label>
                <input
                  type="text"
                  value={barcode}
                  onChange={(e) => setBarcode(e.target.value)}
                  placeholder="Ex : 3606000537583"
                />
              </div>

              <div className="vp-form-group">
                <label>Image principale</label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleMainImageChange}
                />
                <small className="vp-help-text">
                  Format JPG/PNG, taille max ~2-3 Mo.
                </small>
              </div>

              <div className="vp-form-group vp-form-group-full">
                <label>Autres images (galerie)</label>
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleExtraImagesChange}
                />
                <small className="vp-help-text">
                  Vous pouvez ajouter plusieurs images pour montrer le produit
                  sous différents angles.
                </small>
              </div>

              {editingProduct && existingImages.length > 0 && (
                <div className="vp-form-group vp-form-group-full">
                  <label>Images actuelles du produit</label>
                  <div className="vp-images-map">
                    {existingImages.map((img, idx) => (
                      <div key={`${img}-${idx}`} className="vp-images-map-item">
                        <img src={img} alt={`Produit ${idx + 1}`} />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {mainImageFile && (
                <div className="vp-form-group vp-form-group-full">
                  <label>Nouvelle image principale</label>
                  <div className="vp-selected-files">
                    <div className="vp-file-pill">{mainImageFile.name}</div>
                  </div>
                </div>
              )}

              {extraImageFiles.length > 0 && (
                <div className="vp-form-group vp-form-group-full">
                  <label>Nouvelles images supplémentaires</label>
                  <div className="vp-selected-files">
                    {extraImageFiles.map((file, idx) => (
                      <div key={`${file.name}-${idx}`} className="vp-file-pill">
                        {file.name}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="vp-form-group vp-form-group-full">
                <label>Description détaillée</label>
                <textarea
                  rows={4}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Description du produit, avantages, mode d'utilisation, etc."
                />
              </div>

              <div className="vp-form-actions">
                <button
                  type="button"
                  className="vp-btn-secondary"
                  onClick={() => {
                    resetForm();
                    setShowForm(false);
                  }}
                  disabled={saving}
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="vp-btn-primary"
                  disabled={saving}
                >
                  {saving
                    ? "Enregistrement..."
                    : editingProduct
                    ? "Enregistrer les modifications"
                    : "Enregistrer en attente de validation"}
                </button>
              </div>
            </form>
          </section>
        )}

        <section style={{ marginTop: 20 }}>
          <h2
            style={{
              fontSize: 18,
              fontWeight: 700,
              color: "#111827",
              marginBottom: 10,
            }}
          >
            {adminMode ? "Produits listés" : "Tous mes produits"}
          </h2>

          {loadingProducts ? (
            <div style={{ fontSize: 13, color: "#6b7280" }}>
              Chargement des produits...
            </div>
          ) : products.length === 0 ? (
            <div
              style={{
                fontSize: 13,
                color: "#6b7280",
                padding: "14px 0",
              }}
            >
              Aucun produit pour le moment.
              {!adminMode && (
                <>
                  {" "}
                  Cliquez sur <strong>“Ajouter un nouveau produit”</strong> pour
                  commencer.
                </>
              )}
            </div>
          ) : (
            <div className="vp-products-grid">
              {products.map((p) => (
                <article key={p.id} className="vp-product-card">
                  <div className="vp-product-image">
                    {p.ImageUrl ? (
                      <img src={p.ImageUrl} alt={p.name} />
                    ) : (
                      <div className="vp-product-image-placeholder">
                        <span>{p.shortName?.charAt(0) || "P"}</span>
                      </div>
                    )}
                  </div>

                  <div className="vp-product-body">
                    <div className="vp-product-header">
                      <h3 className="vp-product-title">
                        {p.shortName || p.name || p.Name}
                      </h3>
                      {renderStatusBadge(p)}
                    </div>

                    <p className="vp-product-price">
                      {p.SalePrice
                        ? `${p.SalePrice.toLocaleString()} FCFA`
                        : "Prix non défini"}
                    </p>

                    {(p.categoryLabel || p.subCategoryLabel) && (
                      <p className="vp-product-category">
                        {p.categoryLabel}
                        {p.subCategoryLabel
                          ? ` · ${p.subCategoryLabel}`
                          : null}
                      </p>
                    )}

                    {p.barcode && (
                      <p className="vp-product-meta">
                        Code-barres : {p.barcode}
                      </p>
                    )}

                    <p className="vp-product-meta">
                      Stock :{" "}
                      {typeof p.stock === "number" ? p.stock : "Non défini"}
                    </p>

                    {typeof p.Cass_Stock_Price === "number" && (
                      <p className="vp-product-meta">
                        Cass_Stock_Price :{" "}
                        {p.Cass_Stock_Price.toLocaleString()} FCFA
                      </p>
                    )}

                    {p.adminNote && (
                      <p className="vp-product-note">
                        Note admin : {p.adminNote}
                      </p>
                    )}

                    {!adminMode && (
                      <div className="vp-product-actions">
                        <button
                          type="button"
                          className="vp-btn-secondary vp-btn-sm"
                          onClick={() => handleEditClick(p)}
                        >
                          Modifier
                        </button>
                      </div>
                    )}
                  </div>
                </article>
              ))}
            </div>
          )}
        </section>

        <style>{`
          .vp-card {
            background: #ffffff;
            border-radius: 20px;
            border: 1px solid #e5e7eb;
            box-shadow: 0 14px 35px rgba(15,23,42,0.08);
          }

          .vp-form-card {
            padding: 18px 16px 18px;
          }

          .vp-form-title {
            font-size: 16px;
            font-weight: 700;
            color: #111827;
            margin: 0 0 4px;
          }

          .vp-form-subtitle {
            font-size: 13px;
            color: #6b7280;
            margin: 0 0 12px;
          }

          .vp-alert-error {
            margin-bottom: 10px;
            padding: 8px 10px;
            border-radius: 10px;
            background: #fee2e2;
            color: #b91c1c;
            font-size: 13px;
          }

          .vp-form-grid {
            display: grid;
            grid-template-columns: repeat(2, minmax(0, 1fr));
            gap: 12px 16px;
          }

          .vp-form-group {
            display: flex;
            flex-direction: column;
            gap: 4px;
          }

          .vp-form-group-full {
            grid-column: 1 / -1;
          }

          .vp-form-group label {
            font-size: 13px;
            font-weight: 500;
            color: #111827;
          }

          .vp-required {
            color: #dc2626;
          }

          .vp-form-group input,
          .vp-form-group select,
          .vp-form-group textarea {
            border-radius: 10px;
            border: 1px solid #d1d5db;
            padding: 8px 10px;
            font-size: 13px;
            outline: none;
          }

          .vp-form-group input:focus,
          .vp-form-group select:focus,
          .vp-form-group textarea:focus {
            border-color: #4f46e5;
            box-shadow: 0 0 0 1px rgba(79,70,229,0.20);
          }

          .vp-form-group textarea {
            resize: vertical;
          }

          .vp-help-text {
            font-size: 11px;
            color: #6b7280;
          }

          .vp-images-map {
            display: grid;
            grid-template-columns: repeat(auto-fill, minmax(110px, 1fr));
            gap: 10px;
            margin-top: 4px;
          }

          .vp-images-map-item {
            width: 100%;
            aspect-ratio: 1 / 1;
            border-radius: 12px;
            overflow: hidden;
            background: #f8fafc;
            border: 1px solid #e5e7eb;
          }

          .vp-images-map-item img {
            width: 100%;
            height: 100%;
            object-fit: cover;
            display: block;
          }

          .vp-selected-files {
            display: flex;
            flex-wrap: wrap;
            gap: 8px;
            margin-top: 4px;
          }

          .vp-file-pill {
            display: inline-flex;
            align-items: center;
            padding: 6px 10px;
            border-radius: 999px;
            background: #eef2ff;
            color: #3730a3;
            border: 1px solid #c7d2fe;
            font-size: 12px;
            font-weight: 600;
          }

          .vp-form-actions {
            grid-column: 1 / -1;
            display: flex;
            justify-content: flex-end;
            gap: 8px;
            margin-top: 4px;
          }

          .vp-btn-primary,
          .vp-btn-secondary {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            padding: 8px 16px;
            border-radius: 999px;
            border: 1px solid transparent;
            font-size: 13px;
            font-weight: 600;
            cursor: pointer;
          }

          .vp-btn-primary {
            background: #1e17eb;
            color: #ffffff;
            border-color: #1e17eb;
          }

          .vp-btn-primary:hover {
            background: #111827;
          }

          .vp-btn-secondary {
            background: #ffffff;
            color: #111827;
            border-color: #d1d5db;
          }

          .vp-btn-secondary:hover {
            background: #f3f4f6;
          }

          .vp-btn-sm {
            padding: 4px 10px;
            font-size: 12px;
          }

          .vp-products-grid {
            display: grid;
            grid-template-columns: repeat(3, minmax(0, 1fr));
            gap: 16px;
          }

          @media (max-width: 1024px) {
            .vp-products-grid {
              grid-template-columns: repeat(2, minmax(0, 1fr));
            }
          }

          @media (max-width: 640px) {
            .vp-products-grid {
              grid-template-columns: minmax(0, 1fr);
            }

            .vp-form-grid {
              grid-template-columns: minmax(0, 1fr);
            }
          }

          .vp-product-card {
            background: #ffffff;
            border-radius: 18px;
            border: 1px solid #e5e7eb;
            display: flex;
            flex-direction: column;
            overflow: hidden;
          }

          .vp-product-image {
            width: 100%;
            aspect-ratio: 4 / 3;
            background: #f9fafb;
            display: flex;
            align-items: center;
            justify-content: center;
          }

          .vp-product-image img {
            width: 100%;
            height: 100%;
            object-fit: cover;
            display: block;
          }

          .vp-product-image-placeholder {
            width: 100%;
            height: 100%;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 20px;
            font-weight: 700;
            color: #9ca3af;
          }

          .vp-product-body {
            padding: 10px 12px 12px;
          }

          .vp-product-header {
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
            gap: 6px;
            margin-bottom: 4px;
          }

          .vp-product-title {
            font-size: 14px;
            font-weight: 700;
            color: #111827;
            margin: 0;
          }

          .vp-product-price {
            font-size: 14px;
            font-weight: 700;
            color: #16a34a;
            margin: 2px 0;
          }

          .vp-product-category {
            font-size: 12px;
            color: #6b7280;
            margin: 0;
          }

          .vp-product-meta {
            font-size: 12px;
            color: #6b7280;
            margin: 2px 0 0;
          }

          .vp-product-note {
            font-size: 12px;
            color: #b91c1c;
            margin: 4px 0 0;
          }

          .vp-product-actions {
            margin-top: 8px;
            display: flex;
            justify-content: flex-end;
          }

          .vp-status {
            display: inline-flex;
            align-items: center;
            padding: 3px 8px;
            border-radius: 999px;
            font-size: 11px;
            font-weight: 600;
          }

          .vp-status-valid {
            background: #dcfce7;
            color: #166534;
          }

          .vp-status-pending {
            background: #fef9c3;
            color: #92400e;
          }

          .vp-status-refused {
            background: #fee2e2;
            color: #b91c1c;
          }
        `}</style>
      </main>
    </div>
  );
}